import React from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, CircularProgress, Box, Alert, Typography } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { getApiUrl } from './config/api';
import LoginPage from './components/LoginPage';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import Projects from './components/Projects';
import Wallet from './components/Wallet';
import Rewards from './components/Rewards';
import Governance from './components/Governance';
import PointsDistribution from './components/PointsDistribution';
import Blog from './components/Blog';
import Forms from './components/Forms';
import Users from './components/Users';
import Profile from './components/Profile';
import Playground from './components/Playground';
import Family from './components/Family';
import Infrastructure from './components/Infrastructure';
import Storage from './components/Storage';
import Lockers from './components/Lockers';
import WorkSchedule from './components/WorkSchedule';
import VacationSchedule from './components/VacationSchedule';
import WorkPlans from './components/WorkPlans';
import Skaistykla from './components/Skaistykla';
import Exhibition from './components/Exhibition';
import StaffPlans from './components/staffPlans/StaffPlans';
import Admin from './components/Admin';
import Schemos from './components/Schemos';
import Pardavimai from './components/Pardavimai';
import Kontaktai from './components/Kontaktai';
import Stiklainis from './components/Stiklainis';
import Sutartys from './components/Sutartys';
import Dokumentacija from './components/Dokumentacija';
import Mokymai from './components/Mokymai';
import Planetariumas from './components/Planetariumas';
import ForcePasswordChange from './components/ForcePasswordChange';
import './i18n';

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
  },
});

const AppContent: React.FC = () => {
  const { user, loading, token, hasPermission } = useAuth();
  const [visiblePanels, setVisiblePanels] = React.useState<string[]>([]);
  const [visibilityLoading, setVisibilityLoading] = React.useState(true);
  
  const getInitialPage = (): string => {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromQuery = params.get('page');
      if (fromQuery) return fromQuery;
      const fromStorage = window.localStorage.getItem('currentPage');
      if (fromStorage) return fromStorage;
    } catch {}
    return 'blog';
  };
  const [currentPage, setCurrentPage] = React.useState(getInitialPage);
  
  
  // Fetch panel visibility
  React.useEffect(() => {
    if (!user || !token) {
      setVisibilityLoading(false);
      return;
    }
    
    const fetchVisiblePanels = async () => {
      try {
        // Use permission-based endpoint instead of role-based
        const response = await fetch(getApiUrl('/admin/panel-visibility/user'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setVisiblePanels(data.visiblePanels || []);
          } else {
            // Fallback to basic panels if API fails
            setVisiblePanels(['dashboard', 'profile']);
          }
        } else {
          // Fallback to basic panels if API fails
          setVisiblePanels(['dashboard', 'profile']);
        }
      } catch (error) {
        console.error('Error fetching panel visibility:', error);
        // Fallback to basic panels on error
        setVisiblePanels(['dashboard', 'profile']);
      } finally {
        setVisibilityLoading(false);
      }
    };
    
    fetchVisiblePanels();
  }, [user, token]);
  
  // Check if current page is accessible and redirect if not
  // Use useLayoutEffect to run synchronously before browser paints
  // This prevents any flash of unauthorized content
  React.useLayoutEffect(() => {
      if (!visibilityLoading && user && visiblePanels.length > 0) {
      // Always allow dashboard and profile
      if (currentPage === 'dashboard' || currentPage === 'profile') {
        return;
      }
      // Allow permission-based panels even when visibility API failed or returned fallback
      if ((currentPage === 'admin' && (hasPermission('admin.panel_visibility') || hasPermission('admin.permissions'))) ||
          (currentPage === 'sutartys' && hasPermission('contracts.view')) ||
          (currentPage === 'pardavimai' && hasPermission('pardavimai.view')) ||
          (currentPage === 'skaistykla' && hasPermission('complaints.view')) ||
          (currentPage === 'work-plans' && hasPermission('workPlans.view')) ||
          (currentPage === 'staff-plans' && (hasPermission('staff_plans.view') || hasPermission('staff_plans.edit'))) ||
          (currentPage === 'work-schedule' && hasPermission('schedule.view')) ||
          (currentPage === 'mobile-schedule' && hasPermission('schedule.view')) ||
          (currentPage === 'vacation-schedule' && hasPermission('schedule.manage'))) {
        return;
      }
      // Check if current page is in visible panels
      if (!visiblePanels.includes(currentPage)) {
        // Redirect to dashboard immediately if page is not accessible
        // Update URL, localStorage, and state synchronously
        const url = new URL(window.location.href);
        url.searchParams.set('page', 'dashboard');
        window.history.replaceState({}, '', url.toString());
        window.localStorage.setItem('currentPage', 'dashboard');
        setCurrentPage('dashboard');
      }
    }
  }, [currentPage, visiblePanels, visibilityLoading, user]);

  React.useEffect(() => {
    // Only update URL if page is accessible (to avoid flashing)
    if (!visibilityLoading && visiblePanels.length > 0) {
      const isAccessible = currentPage === 'dashboard' || 
                          currentPage === 'profile' || 
                          visiblePanels.includes(currentPage) ||
                          (currentPage === 'admin' && (hasPermission('admin.panel_visibility') || hasPermission('admin.permissions'))) ||
                          (currentPage === 'sutartys' && hasPermission('contracts.view')) ||
                          (currentPage === 'pardavimai' && hasPermission('pardavimai.view')) ||
                          (currentPage === 'skaistykla' && hasPermission('complaints.view')) ||
                          (currentPage === 'work-plans' && hasPermission('workPlans.view')) ||
                          (currentPage === 'staff-plans' && (hasPermission('staff_plans.view') || hasPermission('staff_plans.edit'))) ||
                          (currentPage === 'work-schedule' && hasPermission('schedule.view')) ||
                          (currentPage === 'mobile-schedule' && hasPermission('schedule.view')) ||
                          (currentPage === 'vacation-schedule' && hasPermission('schedule.manage'));
      
      if (isAccessible) {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('page', currentPage);
          window.history.replaceState({}, '', url.toString());
          window.localStorage.setItem('currentPage', currentPage);
        } catch {}
      }
    }
  }, [currentPage, visiblePanels, visibilityLoading]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  // Check if password reset is required (check both localStorage and user object)
  if (localStorage.getItem('passwordResetRequired') === 'true' || user?.passwordResetRequired) {
    return <ForcePasswordChange />;
  }
  
  // Wait for visibility to load before rendering any page
  // This prevents unauthorized pages from flashing
  if (visibilityLoading || visiblePanels.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }
  
  // Check if current page is accessible BEFORE rendering
  // Don't render the page at all if access is denied
  // This check happens synchronously before renderPage() is called
  // Check if page is accessible via panel visibility OR permission-based access
  const isAccessible = currentPage === 'dashboard' || 
                      currentPage === 'profile' || 
                      visiblePanels.includes(currentPage) ||
                      // Permission-based panels (similar to Layout.tsx logic)
                      (currentPage === 'admin' && (hasPermission('admin.panel_visibility') || hasPermission('admin.permissions'))) ||
                      (currentPage === 'sutartys' && hasPermission('contracts.view')) ||
                      (currentPage === 'pardavimai' && hasPermission('pardavimai.view')) ||
                      (currentPage === 'skaistykla' && hasPermission('complaints.view')) ||
                      (currentPage === 'work-plans' && hasPermission('workPlans.view')) ||
                      (currentPage === 'staff-plans' && (hasPermission('staff_plans.view') || hasPermission('staff_plans.edit'))) ||
                      (currentPage === 'work-schedule' && hasPermission('schedule.view')) ||
                      (currentPage === 'mobile-schedule' && hasPermission('schedule.view')) ||
                      (currentPage === 'vacation-schedule' && hasPermission('schedule.manage'));
  
  if (!isAccessible) {
    // Immediately update URL and localStorage (state will update via useEffect)
    // This prevents the page from rendering even briefly
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('page', 'dashboard');
      window.history.replaceState({}, '', url.toString());
      window.localStorage.setItem('currentPage', 'dashboard');
    } catch {}
    
    // Show loading while redirect is processing
    // The useEffect will update currentPage state on next render
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard />;
      case 'projects':
        return <Projects />;
      case 'wallet':
        return <Wallet />;
      case 'rewards':
        return <Rewards />;
      case 'governance':
        return <Governance />;
      case 'points-distribution':
        return <PointsDistribution />;
      case 'blog':
        return <Blog />;
      case 'forms':
        return <Forms />;
      case 'users':
        return <Users />;
      case 'profile':
        return <Profile />;
      case 'playground':
        return <Playground />;
      case 'family':
        return <Family />;
      case 'infrastructure':
        return <Infrastructure />;
      case 'storage':
        return <Storage />;
      case 'lockers':
        return <Lockers />;
      case 'work-schedule':
        return <WorkSchedule />;
      case 'vacation-schedule':
        return <VacationSchedule />;
      case 'mobile-schedule':
        return <WorkSchedule initialView="mobile" />;
      case 'work-plans':
        return <WorkPlans />;
      case 'exhibition':
        return <Exhibition />;
      case 'staff-plans':
        return <StaffPlans />;
      case 'planetariumas':
        return <Planetariumas />;
      case 'schemos':
        return <Schemos />;
      case 'mokymai':
        return <Mokymai />;
      case 'dokumentacija':
        return <Dokumentacija />;
      case 'skaistykla':
        return <Skaistykla />;
      case 'pardavimai':
        return <Pardavimai />;
      case 'kontaktai':
        return <Kontaktai />;
      case 'stiklainis':
        return <Stiklainis />;
      case 'sutartys':
        return <Sutartys />;
      case 'admin':
        return <Admin />;
      default:
        return <Blog />;
    }
  };

  return (
    <Layout currentPage={currentPage} setCurrentPage={setCurrentPage}>
      {renderPage()}
    </Layout>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
